/**
 * DriveLibrarySync — scan/index/diff/import orchestration over an injected
 * DriveClient + injected ports (no store imports; domains-no-store is at
 * error — src/app/google/wireGoogle.ts injects the store-backed adapters).
 *
 * Auth failures are detected with `instanceof GoogleAuthRequiredError`
 * (GG-7), and the silent/interactive split is explicit per call: the boot
 * auto-scan policy stays silent (it can never pop login UI or get the user
 * force-disconnected — GG-2), while user-gesture callers pass
 * { interactive: true }.
 */
import { GoogleAuthRequiredError } from '../auth/errors';
import type { DriveClient, DriveRequestOptions } from './DriveClient';
import type { DriveFile, DriveFileIndex } from './types';

export interface DriveLibrarySyncPorts {
  client: Pick<
    DriveClient,
    'listFilesRecursive' | 'getFolderMetadata' | 'downloadFile'
  >;
  /** useDriveStore adapter. */
  driveIndex: {
    getLinkedFolderId(): string | null;
    getLastScanTime(): number | null;
    getIndex(): DriveFileIndex[];
    setScanning(isScanning: boolean): void;
    setScannedFiles(files: DriveFileIndex[]): void;
  };
  /** useLibraryStore/useBookStore adapter. */
  library: {
    addBook(file: File, options?: { overwrite?: boolean }): Promise<unknown>;
    getLibraryFilenames(): Set<string | undefined>;
  };
  /** The persisted "has connected before" hint (reconnect-affordance copy). */
  hasConnectedBefore(): boolean;
  log?: {
    info(...args: unknown[]): void;
    warn(...args: unknown[]): void;
    error(...args: unknown[]): void;
  };
}

const noopLog = { info: () => {}, warn: () => {}, error: () => {} };

export class DriveLibrarySync {
  private readonly log: NonNullable<DriveLibrarySyncPorts['log']>;

  constructor(private readonly ports: DriveLibrarySyncPorts) {
    this.log = ports.log ?? noopLog;
  }

  /** Scan the linked folder for EPUB files. */
  async scanLinkedFolder(opts: DriveRequestOptions = {}): Promise<DriveFile[]> {
    const linkedFolderId = this.ports.driveIndex.getLinkedFolderId();
    if (!linkedFolderId) {
      this.log.warn('No linked folder ID found.');
      return [];
    }
    try {
      return await this.ports.client.listFilesRecursive(
        linkedFolderId,
        'application/epub+zip',
        opts,
      );
    } catch (error) {
      if (error instanceof GoogleAuthRequiredError) {
        this.log.warn(`Failed to scan linked folder: ${error.message}`);
      } else {
        this.log.error('Failed to scan linked folder:', error);
      }
      throw error;
    }
  }

  /**
   * Download a Drive file and wrap it in a File named after the Drive entry.
   * The ContentMissing restore flow uses this directly so it can hand the File
   * to the bookId-targeted restore path (attaching the binary to the EXACT
   * ghost the user clicked) rather than the book-agnostic importer, which
   * re-discovers the target by filename/title and duplicates the entry on a
   * mismatch.
   */
  async downloadAsFile(
    fileId: string,
    fileName: string,
    opts: DriveRequestOptions = { interactive: true },
  ): Promise<File> {
    this.log.info(`Downloading file: ${fileName} (${fileId})`);
    const blob = await this.ports.client.downloadFile(fileId, opts);
    return new File([blob], fileName, { type: 'application/epub+zip' });
  }

  /** Download a Drive file and import it into the library. */
  async importFile(
    fileId: string,
    fileName: string,
    options?: { overwrite?: boolean },
    opts: DriveRequestOptions = { interactive: true },
  ): Promise<void> {
    try {
      const file = await this.downloadAsFile(fileId, fileName, opts);
      this.log.info(`Importing file to library: ${fileName}`);
      await this.ports.library.addBook(file, options);
    } catch (error) {
      if (error instanceof GoogleAuthRequiredError) {
        this.log.warn(`Failed to import file ${fileName}: ${error.message}`);
      } else {
        this.log.error(`Failed to import file ${fileName}:`, error);
      }
      throw error;
    }
  }

  private static mapToDriveFileIndex(file: DriveFile): DriveFileIndex {
    return {
      id: file.id,
      name: file.name,
      size: parseInt(file.size || '0', 10),
      modifiedTime: file.modifiedTime || new Date().toISOString(),
      mimeType: file.mimeType,
      // Carry Drive's content MD5 through to the persisted index so the
      // partial-fetch preview cache can key on {fileId, md5}.
      md5Checksum: file.md5Checksum,
    };
  }

  /** Full scan of the linked folder; updates the persisted index. */
  async scanAndIndex(opts: DriveRequestOptions = {}): Promise<void> {
    const linkedFolderId = this.ports.driveIndex.getLinkedFolderId();
    if (!linkedFolderId) {
      this.log.warn('No linked folder ID found.');
      return;
    }
    try {
      this.ports.driveIndex.setScanning(true);
      this.log.info('Starting full drive scan...');
      const rawFiles = await this.ports.client.listFilesRecursive(
        linkedFolderId,
        'application/epub+zip',
        opts,
      );
      const index = rawFiles.map(DriveLibrarySync.mapToDriveFileIndex);
      this.log.info(`Scan complete. Indexed ${index.length} files.`);
      this.ports.driveIndex.setScannedFiles(index);
    } catch (error) {
      if (error instanceof GoogleAuthRequiredError) {
        this.log.warn(`Failed to scan and index: ${error.message}`);
      } else {
        this.log.error('Failed to scan and index:', error);
      }
      throw error;
    } finally {
      this.ports.driveIndex.setScanning(false);
    }
  }

  /**
   * Diff the cloud index against the local library; scans first when the
   * index is empty.
   */
  async checkForNewFiles(opts: DriveRequestOptions = {}): Promise<DriveFileIndex[]> {
    let index = this.ports.driveIndex.getIndex();
    if (index.length === 0) {
      await this.scanAndIndex(opts);
      index = this.ports.driveIndex.getIndex();
    }

    const libraryFilenames = this.ports.library.getLibraryFilenames();
    const newFiles = index.filter((f) => !libraryFilenames.has(f.name));
    this.log.info(`Diff logic: Found ${newFiles.length} new files available for import.`);
    return newFiles;
  }

  /**
   * Auto-sync heuristic: linked + connected-before + (never scanned, or the
   * folder was viewed since the last scan). ALWAYS silent: an unavailable
   * token means "don't sync" — never a popup, never a disconnect.
   */
  async shouldAutoSync(): Promise<boolean> {
    const linkedFolderId = this.ports.driveIndex.getLinkedFolderId();
    if (!linkedFolderId) return false;
    if (!this.ports.hasConnectedBefore()) return false;

    const lastScanTime = this.ports.driveIndex.getLastScanTime();
    if (!lastScanTime) return true;

    try {
      const metadata = await this.ports.client.getFolderMetadata(linkedFolderId, {
        interactive: false,
      });
      const viewedTime = metadata.viewedByMeTime
        ? new Date(metadata.viewedByMeTime).getTime()
        : 0;
      return viewedTime > lastScanTime;
    } catch (error) {
      if (error instanceof GoogleAuthRequiredError) {
        this.log.warn(`Auto-sync heuristic: token unavailable (${error.message}).`);
        return false;
      }
      this.log.warn('Failed to check folder metadata for auto-sync heuristic:', error);
      // Default to true on unknown errors to be safe.
      return true;
    }
  }
}
